<template>
  <Card class="border-border shadow-sm flex flex-col h-full">
    <CardHeader>
      <CardTitle class="text-xl font-bold">Performance du Placement</CardTitle>
    </CardHeader>
    <CardContent class="flex-1 flex flex-col space-y-6">
      <div class="space-y-4">
        <div class="flex justify-between items-center group">
          <span class="text-sm text-muted-foreground group-hover:text-zinc-600 transition-colors">Investi (Sorti de votre poche) :</span>
          <span class="text-lg font-bold tabular-nums" :class="{ 'blur-sm select-none': isAmountsHidden }">{{ formatAmount(investedAmount) }}€</span>
        </div>
        
        <div class="pt-4 border-t border-border flex justify-between items-center">
          <span class="text-sm font-semibold text-foreground">Valorisation Actuelle :</span>
          <span class="text-2xl font-black text-emerald-600 tabular-nums tracking-tight" :class="{ 'blur-sm select-none': isAmountsHidden }">
            {{ formatAmount(currentValue) }}€
          </span>
        </div>

        <div class="mt-4 p-4 rounded-xl border transition-all duration-300 flex justify-between items-center" 
             :class="plusValue >= 0 ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'">
          <div class="space-y-1">
            <span class="text-xs font-bold uppercase tracking-wider" :class="plusValue >= 0 ? 'text-emerald-700' : 'text-red-700'">
              {{ plusValue >= 0 ? 'Plus-value globale' : 'Moins-value globale' }}
            </span>
            <div v-if="annualizedRate !== null" class="text-xs font-medium bg-background/60 px-2 py-0.5 rounded-full w-fit flex items-center gap-1"
                 :class="annualizedRate >= 0 ? 'text-emerald-500' : 'text-red-500'">
              <TrendingUp v-if="annualizedRate >= 0" class="w-3 h-3" />
              <TrendingDown v-else class="w-3 h-3" />
              {{ annualizedRate >= 0 ? '+' : '' }}{{ formatAmount(annualizedRate) }}% / an
            </div>
          </div>
          <div class="text-right">
            <div class="text-2xl font-black tabular-nums" :class="[plusValue >= 0 ? 'text-emerald-500' : 'text-red-500', { 'blur-sm select-none': isAmountsHidden }]">
              {{ plusValue >= 0 ? '+' : '-' }}{{ formatAmount(Math.abs(plusValue)) }}€
            </div>
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="space-y-3 pt-4 border-t border-border mt-auto">
        <Button variant="finance" class="w-full h-11" @click="$emit('add-movement')">
          <Plus class="w-4 h-4 mr-2" /> Ajouter un Mouvement
        </Button>
        <Button variant="outline" class="w-full h-11 border-primary/20 hover:bg-primary/5 text-primary" @click="$emit('update-valuation')">
          <RefreshCw class="w-4 h-4 mr-2" /> Mettre à jour la valorisation
        </Button>
        
        <div class="grid grid-cols-2 gap-2 mt-4">
          <!-- The hidden input for CSV is kept in the parent for simplicity or here? 
               Let's keep the logic here and emit 'request-csv-upload'. 
          -->
          <Button variant="ghost" size="sm" class="text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted" @click="$emit('import-csv')">
            <FileDown class="w-3.5 h-3.5 mr-2" /> Import CSV
          </Button>
          <Button variant="ghost" size="sm" class="text-xs font-medium text-red-400 hover:text-red-500 hover:bg-red-500/10" @click="$emit('clear-history')">
            <Trash2 class="w-3.5 h-3.5 mr-2" /> Vider
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
</template>

<script setup>
import { computed } from 'vue'
import { TrendingUp, TrendingDown, Plus, RefreshCw, FileDown, Trash2 } from 'lucide-vue-next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { formatAmount } from '@/lib/utils'

const props = defineProps({
  investedAmount: { type: [Number, String], required: true },
  currentValue: { type: [Number, String], required: true },
  annualizedRate: { type: Number, default: null },
  isAmountsHidden: { type: Boolean, default: false }
})

const plusValue = computed(() => parseFloat(props.currentValue) - parseFloat(props.investedAmount))

defineEmits(['add-movement', 'update-valuation', 'import-csv', 'clear-history'])
</script>
